import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Gift,
  Headset,
  History,
  Home,
  Settings,
  UserRound,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { useUiStore } from '@/stores/ui.js';
import { useAuthStore } from '@/stores/auth.js';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip.js';
import { SPIN_QUERY_KEYS, fetchSpinEvent, hasSpinWaiting } from '@/features/spin/api.js';

interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
}

// bc.game-style rail: the primary play destinations are standalone cards,
// account/utility items live inside grouped containers below.
const PLAY_ITEMS: NavItem[] = [
  { to: '/', labelKey: 'nav.home', icon: Home },
];

// Redeem, referral and the wheel are tabs of the bonus hub, so the hub is the
// rail's one rewards entry.
const BONUS_ITEM: NavItem = { to: '/promos', labelKey: 'nav.promo', icon: Gift };

const ACCOUNT_ITEMS: NavItem[] = [
  { to: '/wallet',          labelKey: 'nav.wallet',     icon: Wallet          },
  { to: '/wallet/deposit',  labelKey: 'common.deposit', icon: ArrowDownToLine },
  { to: '/wallet/withdraw', labelKey: 'common.withdraw',icon: ArrowUpFromLine },
  { to: '/history',         labelKey: 'nav.history',    icon: History         },
];

const FOOTER_ITEMS: NavItem[] = [
  { to: '/support',  labelKey: 'nav.support',  icon: Headset   },
  { to: '/profile',  labelKey: 'nav.profile',  icon: UserRound },
  { to: '/settings', labelKey: 'nav.settings', icon: Settings  },
];

interface SidebarProps {
  currentPath: string;
}

/**
 * Full-height left rail (bc.game style). Sibling of the main column in
 * AppShell; the expand/collapse toggle lives in the TopBar next to the logo.
 * Expanded (~224px): play items as standalone cards, account/footer items in
 * grouped containers. Collapsed (~68px): icon squares; hovering shows a
 * floating label tooltip (Radix-portaled, escaping the backdrop-filter clip).
 * Collapsed state persists in the ui store.
 */
export function Sidebar({ currentPath }: SidebarProps) {
  const { t } = useTranslation('common');
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const isAuthed = Boolean(useAuthStore((s) => s.accessToken));

  // The bonus entry pulses while the player has a Lucky Spin waiting. Guest
  // desktop sessions (public lobby) must not fire this authed probe.
  const spinLucky = hasSpinWaiting(
    useQuery({
      queryKey: SPIN_QUERY_KEYS.event,
      queryFn: fetchSpinEvent,
      enabled: isAuthed,
      staleTime: 15_000,
    }).data,
  );

  const all = [...PLAY_ITEMS, BONUS_ITEM, ...ACCOUNT_ITEMS, ...FOOTER_ITEMS];
  const activeTo = all
    .map((i) => i.to)
    .filter((to) => currentPath === to || currentPath.startsWith(to + '/'))
    .sort((a, b) => b.length - a.length)[0];

  // Collapsed: square icon button + Radix hover tooltip (portaled to escape
  // the rail's backdrop-filter containing block).
  const renderCollapsed = (item: NavItem, lucky = false) => {
    const active = item.to === activeTo;
    const ItemIcon = item.icon;
    return (
      <Tooltip key={item.to}>
        <TooltipTrigger asChild>
          <Link
            to={item.to as '/'}
            aria-label={t(item.labelKey)}
            aria-current={active ? 'page' : undefined}
            className={[
              'grid size-10 shrink-0 place-items-center rounded-xl transition-colors',
              active
                ? 'bg-[var(--accent)]/18 text-[var(--accent-strong)]'
                : lucky
                  ? 'bg-[var(--accent)]/10 text-[var(--accent-strong)] ring-1 ring-[var(--accent)]/40 animate-pulse'
                  : 'bg-[var(--bg-elevated)]/55 text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)]',
            ].join(' ')}
          >
            <ItemIcon className="size-[19px]" aria-hidden />
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right">{t(item.labelKey)}</TooltipContent>
      </Tooltip>
    );
  };

  // Expanded, standalone card per item (play group).
  const renderCard = (item: NavItem) => {
    const active = item.to === activeTo;
    const ItemIcon = item.icon;
    return (
      <Link
        key={item.to}
        to={item.to as '/'}
        aria-current={active ? 'page' : undefined}
        className={[
          'flex h-11 items-center gap-3 rounded-xl px-3 text-[14px] font-bold transition-colors',
          active
            ? 'bg-[var(--accent)]/15 text-[var(--text-primary)]'
            : 'bg-[var(--bg-elevated)]/55 text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)]',
        ].join(' ')}
      >
        <ItemIcon
          className={['size-[19px] shrink-0', active ? 'text-[var(--accent-strong)]' : 'text-[var(--text-muted)]'].join(' ')}
          aria-hidden
        />
        <span className="truncate">{t(item.labelKey)}</span>
      </Link>
    );
  };

  // Expanded, flat row inside a grouped container (account / footer groups).
  const renderRow = (item: NavItem, lucky = false) => {
    const active = item.to === activeTo;
    const ItemIcon = item.icon;
    return (
      <Link
        key={item.to}
        to={item.to as '/'}
        aria-current={active ? 'page' : undefined}
        className={[
          'flex h-10 items-center gap-3 rounded-lg px-2.5 text-[13px] font-semibold transition-colors',
          active
            ? 'bg-[var(--accent)]/15 text-[var(--text-primary)]'
            : lucky
              ? 'bg-[var(--accent)]/10 text-[var(--text-primary)] ring-1 ring-[var(--accent)]/40 animate-pulse'
              : 'text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)]',
        ].join(' ')}
      >
        <ItemIcon
          className={[
            'size-[17px] shrink-0',
            active || lucky ? 'text-[var(--accent-strong)]' : 'text-[var(--text-muted)]',
          ].join(' ')}
          aria-hidden
        />
        <span className="truncate">{t(item.labelKey)}</span>
      </Link>
    );
  };

  const groupCls = collapsed
    ? 'flex flex-col items-center gap-1.5 rounded-2xl bg-[var(--bg-raised)]/45 p-1.5'
    : 'flex flex-col gap-0.5 rounded-2xl bg-[var(--bg-raised)]/45 p-1.5';

  return (
    <TooltipProvider delayDuration={200}>
      <aside
        className={[
          'hidden lg:flex flex-col gap-3 shrink-0 sticky top-20 h-[calc(100vh-5rem)] z-20',
          'bg-[var(--bg-sunken)]/80 backdrop-blur-md border-r border-[var(--border-subtle)]',
          'py-3 overflow-y-auto bf-noscroll transition-[width] duration-200',
          collapsed ? 'w-[68px] px-2.5' : 'w-56 px-3',
        ].join(' ')}
      >
        {collapsed ? (
          <div className="flex flex-col items-center gap-1.5">{PLAY_ITEMS.map((item) => renderCollapsed(item))}</div>
        ) : (
          <div className="flex flex-col gap-1.5">{PLAY_ITEMS.map((item) => renderCard(item))}</div>
        )}

        <div className={groupCls}>
          {collapsed ? renderCollapsed(BONUS_ITEM, spinLucky) : renderRow(BONUS_ITEM, spinLucky)}
        </div>

        <div className={groupCls}>
          {ACCOUNT_ITEMS.map((item) => (collapsed ? renderCollapsed(item) : renderRow(item)))}
        </div>

        <div className="flex-1" />

        <div className={groupCls}>
          {FOOTER_ITEMS.map((item) => (collapsed ? renderCollapsed(item) : renderRow(item)))}
        </div>
      </aside>
    </TooltipProvider>
  );
}
